import React, { useState, useMemo } from 'react';
import { WaitRecord } from '../../../types';
import { computeWaitAccuracy, exportWaitRecordsToCSV } from '../../../services/auditService';
import { useStore } from '../../../store';
import { KPI, Th, Td, tbStyle } from './AuditTablePrimitives';
import { pct } from '../utils/formatters';

const WAIT_CLASS_COLORS: Record<string, string> = {
  correct_avoidance: 'var(--green)', missed_opportunity: 'var(--red)',
  neutral: 'var(--t3)', pending: 'var(--amber)',
};

function WaitRecordEditor({ record, onSave }: { record: WaitRecord; onSave: (r: WaitRecord) => void }) {
  const [cls,   setCls]   = useState(record.classification);
  const [notes, setNotes] = useState(record.notes);
  const inp = { padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 11, fontFamily: 'var(--mono)' };
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>CLASSIFICATION</div>
        <select value={cls} onChange={e => setCls(e.target.value as any)} style={{ ...inp, width: 180 }}>
          <option value="pending">pending</option>
          <option value="correct_avoidance">correct avoidance</option>
          <option value="missed_opportunity">missed opportunity</option>
          <option value="neutral">neutral</option>
        </select>
      </div>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>NOTES</div>
        <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add context…"
          style={{ ...inp, width: '100%' }} />
      </div>
      <button onClick={() => onSave({ ...record, classification: cls, notes })}
        style={{ padding: '5px 14px', background: 'var(--cyan)20', border: '1px solid var(--cyan)',
          borderRadius: 4, color: 'var(--cyan)', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
        Save
      </button>
    </div>
  );
}

function WaitLogQuickAdd({ onAdd }: { onAdd: (r: WaitRecord) => void }) {
  const { addWaitRecord } = useStore();
  const [open,   setOpen]   = useState(false);
  const [symbol, setSymbol] = useState('');
  const [reason, setReason] = useState('');
  const [setup,  setSetup]  = useState('');
  const [dir,    setDir]    = useState<'long' | 'short' | 'both'>('both');

  const inp = { padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 4, color: 'var(--t1)', fontSize: 11, fontFamily: 'var(--mono)' };

  const submit = () => {
    if (!symbol || !reason) return;
    const rec: WaitRecord = {
      id: `wait_${symbol}_${Date.now()}`, symbol: symbol.toUpperCase(),
      timestamp: Date.now(), reason, regimeAtTime: 'unknown', phaseAtTime: 'RANGE',
      setupTypeAvoided: setup || 'unspecified', directionAvoided: dir,
      classification: 'pending', priceAtTime: null, notes: '',
    };
    addWaitRecord(rec);
    setSymbol(''); setReason(''); setSetup(''); setOpen(false);
  };

  return (
    <div style={{ marginTop: 12 }}>
      {!open ? (
        <button onClick={() => setOpen(true)}
          style={{ padding: '5px 14px', background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 4, color: 'var(--t2)', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' }}>
          + Log Wait / No-Trade
        </button>
      ) : (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap',
          padding: 10, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6 }}>
          <div>
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>SYMBOL</div>
            <input value={symbol} onChange={e => setSymbol(e.target.value)} placeholder="NVDA" style={{ ...inp, width: 70 }} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>SETUP AVOIDED</div>
            <input value={setup} onChange={e => setSetup(e.target.value)} placeholder="Continuation Long…" style={{ ...inp, width: 160 }} />
          </div>
          <div>
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>DIRECTION</div>
            <select value={dir} onChange={e => setDir(e.target.value as any)} style={{ ...inp, width: 90 }}>
              <option value="long">long</option>
              <option value="short">short</option>
              <option value="both">both</option>
            </select>
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 8, color: 'var(--t3)', marginBottom: 2 }}>REASON FOR PASSING</div>
            <input value={reason} onChange={e => setReason(e.target.value)}
              placeholder="Low RVOL, midday, no confirmation…" style={{ ...inp, width: '100%' }} />
          </div>
          <button onClick={submit}
            style={{ padding: '5px 12px', background: 'var(--cyan)20', border: '1px solid var(--cyan)',
              borderRadius: 4, color: 'var(--cyan)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--mono)' }}>
            Save
          </button>
          <button onClick={() => setOpen(false)}
            style={{ padding: '5px 10px', background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 4, color: 'var(--t3)', fontSize: 11, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export function WaitLogView({ records, onUpdate, onDelete }: {
  records: WaitRecord[];
  onUpdate: (r: WaitRecord) => void;
  onDelete: (id: string) => void;
}) {
  const [editId,    setEditId]    = useState<string | null>(null);
  const [filterSym, setFilterSym] = useState('');
  const accuracy = useMemo(() => computeWaitAccuracy(records), [records]);

  const downloadCSV = () => {
    const blob = new Blob([exportWaitRecordsToCSV(records)], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `mer-wait-${Date.now()}.csv`; a.click();
  };

  const filtered = records
    .filter(r => !filterSym || r.symbol.toLowerCase().includes(filterSym.toLowerCase()))
    .sort((a, b) => b.timestamp - a.timestamp);

  const btnS = (active: boolean) => ({
    padding: '3px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 10,
    fontFamily: 'var(--mono)', background: active ? 'var(--cyan)20' : 'var(--surface)',
    border: `1px solid ${active ? 'var(--cyan)' : 'var(--border)'}`,
    color: active ? 'var(--cyan)' : 'var(--t2)',
  });

  if (!records.length) {
    return (
      <div style={{ padding: 24, color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>No wait records yet</div>
        <div style={{ fontSize: 12, color: 'var(--t3)', maxWidth: 420, margin: '0 auto' }}>
          Wait records track conscious no-trade decisions. Use the "Log Wait" button below
          to record when you consciously pass on a setup.
        </div>
        <WaitLogQuickAdd onAdd={onUpdate} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <KPI label="Total Waits"    value={String(accuracy.total)} />
        <KPI label="Correct Avoid." value={String(accuracy.correctAvoidances)} color="var(--green)" />
        <KPI label="Missed Opps."   value={String(accuracy.missedOpportunities)} color="var(--red)" />
        <KPI label="Pending"        value={String(accuracy.pending)}             color="var(--amber)" />
        <KPI label="Avoid Rate"
          value={accuracy.avoidanceRate !== null ? pct(accuracy.avoidanceRate) : '—'}
          color={accuracy.avoidanceRate !== null
            ? (accuracy.avoidanceRate >= 0.6 ? 'var(--green)' : accuracy.avoidanceRate >= 0.4 ? 'var(--amber)' : 'var(--red)')
            : 'var(--t3)'}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input value={filterSym} onChange={e => setFilterSym(e.target.value)}
          placeholder="Filter symbol…" style={{ padding: '4px 8px', background: 'var(--surface2)',
            border: '1px solid var(--border)', borderRadius: 4, color: 'var(--t1)',
            fontSize: 11, fontFamily: 'var(--mono)', width: 110 }} />
        <span style={{ fontSize: 10, color: 'var(--t3)', marginLeft: 'auto' }}>{filtered.length} records</span>
        <button onClick={downloadCSV} style={btnS(false)}>↓ CSV</button>
      </div>

      <table style={tbStyle}>
        <thead>
          <tr>
            <Th>Symbol</Th><Th>Setup Avoided</Th><Th>Dir</Th><Th>Regime</Th>
            <Th>Reason</Th><Th>Classification</Th><Th>Time</Th>
            <th style={{ padding: '5px 10px', borderBottom: '1px solid var(--border)' }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(r => (
            <React.Fragment key={r.id}>
              <tr>
                <Td><span style={{ fontWeight: 700 }}>{r.symbol}</span></Td>
                <Td><span style={{ fontSize: 9 }}>{r.setupTypeAvoided.substring(0, 18)}</span></Td>
                <Td><span style={{ fontSize: 9, color: r.directionAvoided === 'long' ? 'var(--green)' : r.directionAvoided === 'short' ? 'var(--red)' : 'var(--t3)' }}>{r.directionAvoided}</span></Td>
                <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{r.regimeAtTime.substring(0, 12)}</span></Td>
                <Td><span style={{ fontSize: 9 }}>{r.reason.substring(0, 30)}</span></Td>
                <Td>
                  <span style={{ fontSize: 9, fontWeight: 700, color: WAIT_CLASS_COLORS[r.classification] ?? 'var(--t2)' }}>
                    {r.classification.replace('_', ' ')}
                  </span>
                </Td>
                <Td><span style={{ fontSize: 9, color: 'var(--t3)' }}>{new Date(r.timestamp).toLocaleTimeString()}</span></Td>
                <Td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => setEditId(editId === r.id ? null : r.id)}
                      style={{ ...btnS(editId === r.id), fontSize: 9 }}>
                      {editId === r.id ? 'Close' : 'Edit'}
                    </button>
                    <button onClick={() => onDelete(r.id)}
                      style={{ ...btnS(false), fontSize: 9, color: 'var(--red)' }}>✕</button>
                  </div>
                </Td>
              </tr>
              {editId === r.id && (
                <tr>
                  <td colSpan={8} style={{ padding: '8px 10px', background: 'var(--surface2)',
                    borderBottom: '1px solid var(--border)' }}>
                    <WaitRecordEditor record={r} onSave={updated => { onUpdate(updated); setEditId(null); }} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <WaitLogQuickAdd onAdd={onUpdate} />
    </div>
  );
}
